<template>
  <v-container class="pa-2 motorista-screen" fluid>
    <v-toolbar dense flat color="primary" dark class="mb-2 rounded">
      <v-toolbar-title class="text-subtitle-1 font-weight-medium">
        {{ $t('guiasMotorista.title') }}
      </v-toolbar-title>
      <v-switch
        v-model="showAllTripsToday"
        class="ml-2 mt-0 pt-0"
        inset
        dense
        hide-details
        :label="$t('guiasMotorista.showAllTripsToday')"
        color="white"
      />
      <v-spacer />
      <v-btn icon :title="$t('guide.reload')" @click="loadGuias" :loading="loading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
      <v-btn icon :title="$t('guiasMotorista.historyTitle')" @click="openHistorico">
        <v-icon>mdi-history</v-icon>
      </v-btn>
      <v-btn icon :title="$t('button.logout')" @click="logout">
        <v-icon>mdi-logout</v-icon>
      </v-btn>
    </v-toolbar>

    <div>
      <v-alert v-if="!idFuncionario" type="warning" dense outlined class="mb-3">
        {{ $t('guiasMotorista.noDriverLinked') }}
      </v-alert>

      <v-alert v-if="modoSomenteLeitura && idFuncionario" type="info" dense outlined class="mb-3">
        {{ $t('guiasMotorista.viewOnlyWithoutJornada') }}
      </v-alert>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

      <v-list v-if="!loading && guias.length === 0 && idFuncionario" two-line class="transparent">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>{{ $t('guiasMotorista.emptyToday') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-card
        v-for="g in guias"
        :key="g.id_guia"
        class="mb-3"
        outlined
        :class="{ 'guia-card--checkin-ativo': isGuiaComCheckinAtivo(g) }"
        style="cursor: pointer;"
        @click="openGuiaDetalhe(g)"
      >
        <v-card-text class="py-3">
          <div class="text-h6 font-weight-medium">{{ g.nome_linha }}</div>
          <v-row dense class="mt-2">
            <v-col cols="6">
              <span class="text-caption text--secondary">{{ $t('datetime.from') }}</span>
              <div class="text-h6 font-weight-medium">{{ g.dt_pegada }}</div>
            </v-col>
            <v-col cols="6">
              <span class="text-caption text--secondary">{{ $t('guiasMotorista.vehicle') }}</span>
              <div class="text-h6 font-weight-medium">{{ g.cod_veiculo }} · {{ g.placa }}</div>
            </v-col>
            <v-col cols="6">
              <span class="text-caption text--secondary">{{ $t('guide.status') }}</span>
              <div class="text-body-2">{{ g.status_extenso }}</div>
            </v-col>
            <v-col cols="6">
              <span class="text-caption text--secondary">{{ $t('datetime.until') }}</span>
              <div class="text-body-2">{{ getDtLargadaDisplay(g) || '—' }}</div>
            </v-col>
          <div class="d-flex align-center flex-wrap" style="gap: 8px; width: 100%;">
            <div class="text--secondary">{{ g.descricao_curta || g.id_guia }}</div>
            <v-chip
              v-if="isGuiaComCheckinAtivo(g)"
              x-small
              color="success"
              text-color="white"
              class="font-weight-medium"
              style="margin-left: auto; margin-right: 0;"
            >
              {{ $t('guiasMotorista.checkinActiveBadge') }}
            </v-chip>
          </div>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <v-dialog v-model="dialogDetalhe" fullscreen :retain-focus="false">
      <v-card class="guia-detalhe-card">
        <v-toolbar dense flat color="primary" dark>
          <v-btn icon :title="$t('button.close')" @click="closeGuiaDetalhe">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-spacer />
          <v-toolbar-title class="text-subtitle-1 font-weight-medium guia-detalhe-toolbar-title text-center">
            {{ (guiaDetalhe && (guiaDetalhe.descricao_curta || guiaDetalhe.guia_original || guiaDetalhe.id_guia)) || $t('guiasMotorista.tripDetails') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            v-if="motoristaNavigationMapButtonVisible"
            icon
            :title="$t('guiasMotorista.openNavigationMap')"
            @click.stop="openMotoristaNavigationMap"
          >
            <v-icon>mdi-navigation</v-icon>
          </v-btn>
          <div v-else class="guia-detalhe-toolbar-nav-placeholder" aria-hidden="true" />
        </v-toolbar>

        <v-progress-linear v-if="loadingDetalhe" indeterminate color="primary" />

        <v-card-text class="pa-3 guia-detalhe-body">
          <div v-if="guiaDetalhe" class="mb-3">
            <div class="text-body-2 text--secondary">{{ guiaDetalhe.nome_linha }}</div>
            <div class="text-caption text--secondary">
              {{ $t('datetime.from') }}: {{ guiaDetalhe.dt_pegada || '—' }} · {{ $t('datetime.until') }}:
              {{ getDtLargadaDisplay(guiaDetalhe) || '—' }}
            </div>
            <div v-if="guiaDetalhe && !loadingDetalhe" class="text-caption text--secondary mt-1">
              {{ $t('guiasMotorista.checkinLabel') }}: {{ guiaDetalhe.dt_checkin_motorista_fmt || '—' }} ·
              {{ $t('guiasMotorista.checkoutLabel') }}: {{ guiaDetalhe.dt_checkout_motorista_fmt || '—' }}
            </div>
          </div>

          <v-alert v-if="!loadingDetalhe && guiaDetalhe && (guiaDetalhe.id_linha == null)" type="warning" dense outlined class="mb-3">
            {{ $t('guiasMotorista.noLineLinked') }}
          </v-alert>

          <v-alert
            v-if="!loadingDetalhe && guiaDetalhe && modoSomenteLeitura"
            type="info"
            dense
            outlined
            class="mb-3"
          >
            {{ $t('guiasMotorista.viewOnlyWithoutJornada') }}
          </v-alert>

          <v-alert
            v-if="!loadingDetalhe && guiaDetalhe && motoristaTripCheckPhase === 'checkin_blocked'"
            type="warning"
            dense
            outlined
            class="mb-3"
          >
            {{ checkinBlockedMessage }}
          </v-alert>

          <div
            v-if="guiaDetalhe && !loadingDetalhe"
            class="mb-2 d-flex align-center justify-space-between flex-wrap"
            style="gap: 8px; width: 100%;"
          >
            <v-btn
              v-if="motoristaTripCheckButtonVisible"
              small
              outlined
              color="primary"
              :disabled="motoristaTripCheckButtonDisabled"
              :loading="checkSalvando"
              @click.stop="motoristaTripCheckTap"
            >
              <v-icon left small>{{ motoristaTripCheckButtonIcon }}</v-icon>
              {{ motoristaTripCheckButtonLabel }}
            </v-btn>
            <v-card class="pa-1" style="min-width: 120px; max-width: 180px; margin-left: auto; margin-right: 0;">
              <v-card-text class="pa-1" style="padding: 4px !important;">
                <v-row justify="center" align="center" no-gutters>
                  <v-col
                    cols="6"
                    class="pa-0 text-center"
                    @click="setSentido(1)"
                    @keydown.enter="setSentido(1)"
                    style="cursor:pointer;"
                    tabindex="0"
                    role="button"
                    :aria-label="`Sentido ${$t('guiasMotorista.directionIda')} ${sentidoSelecionado === 1 ? 'selecionado' : ''}`"
                  >
                    <v-icon :color="sentidoSelecionado === 1 ? 'green' : 'grey'" small style="font-size: 20px;" aria-hidden="true">
                      mdi-bus
                    </v-icon>
                    <div aria-hidden="true" style="font-size: 12px; font-weight: bold; line-height: 1;">
                      {{ $t('guiasMotorista.directionIda') }}
                    </div>
                  </v-col>
                  <v-col
                    cols="6"
                    class="pa-0 text-center"
                    @click="setSentido(2)"
                    @keydown.enter="setSentido(2)"
                    style="cursor:pointer;"
                    tabindex="0"
                    role="button"
                    :aria-label="`Sentido ${$t('guiasMotorista.directionVolta')} ${sentidoSelecionado === 2 ? 'selecionado' : ''}`"
                  >
                    <v-icon :color="sentidoSelecionado === 2 ? 'orange' : 'grey'" small style="font-size: 20px;" aria-hidden="true">
                      mdi-bus
                    </v-icon>
                    <div aria-hidden="true" style="font-size: 12px; font-weight: bold; line-height: 1;">
                      {{ $t('guiasMotorista.directionVolta') }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>

          <div
            v-if="!loadingDetalhe && (rotasDetalhe.length || paradasDetalhe.length)"
            class="guia-detalhe-content"
            :class="{ 'guia-detalhe-content--wide': guiaDetalheWideLayout }"
          >
            <div class="guia-detalhe-map-container">
              <l-map
                ref="guiaMap"
                :key="`guia-map-${sentidoSelecionado}`"
                :zoom="14"
                :center="guiaMapCenter"
                :options="mapOptions"
                class="guia-detalhe-map"
                @ready="onGuiaMapReady"
              >
                <l-tile-layer :url="tileUrl" :attribution="tileAttribution" />

                <l-geo-json
                  v-for="(rota, idx) in rotasFiltradas"
                  :key="`rota-${rota.id_rota || idx}`"
                  :geojson="rota.geojson"
                  :options-style="rotaStyle"
                />

                <l-marker
                  v-for="(p, idx) in paradasFiltradas"
                  :key="`parada-${idx}`"
                  :lat-lng="[Number(p.lat_parada), Number(p.lng_parada)]"
                  @click="focusStop(p)"
                >
                  <l-tooltip :content="paradaTooltip(p)" />
                </l-marker>
              </l-map>

              <v-btn
                v-if="!guiaDetalheWideLayout && paradasFiltradas.length"
                fab
                small
                color="primary"
                class="guia-stops-fab"
                :title="$t('guiasMotorista.stopsTitle')"
                @click.stop="stopsDrawerOpen = true"
              >
                <v-icon>mdi-bus-stop</v-icon>
              </v-btn>
            </div>

            <aside v-if="guiaDetalheWideLayout" class="guia-detalhe-stops-panel">
              <div class="text-subtitle-2 font-weight-medium mb-2">{{ $t('guiasMotorista.stopsTitle') }}</div>
              <div class="stops-scroll">
                <v-list two-line dense class="py-0">
                  <v-list-item
                    v-for="(p, idx) in paradasFiltradas"
                    :key="`stop-side-${idx}`"
                    style="cursor: pointer;"
                    @click="focusStop(p)"
                  >
                    <v-list-item-content>
                      <v-list-item-title style="font-size: 15px;">
                        {{ p.cod_parada != null ? p.cod_parada : (idx + 1) }}
                        <span v-if="p.descricao_sentido"> · {{ p.descricao_sentido }}</span>
                      </v-list-item-title>
                      <v-list-item-subtitle style="font-size: 15px; overflow: break-word; white-space: normal;">
                        {{ p.endereco_parada }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </aside>
          </div>

          <v-alert v-if="!loadingDetalhe && guiaDetalhe && guiaDetalhe.id_linha != null && rotasDetalhe.length === 0" type="info" dense outlined class="mt-3">
            {{ $t('guiasMotorista.noRouteFound') }}
          </v-alert>

          <v-alert v-if="!loadingDetalhe && guiaDetalhe && guiaDetalhe.id_linha != null && paradasDetalhe.length === 0" type="info" dense outlined class="mt-3">
            {{ $t('guiasMotorista.noStopsFound') }}
          </v-alert>
        </v-card-text>

        <v-navigation-drawer
          v-if="dialogDetalhe && !guiaDetalheWideLayout"
          v-model="stopsDrawerOpen"
          absolute
          temporary
          right
          :width="stopsDrawerWidth"
          class="guia-stops-drawer"
        >
          <v-toolbar dense flat color="primary" dark>
            <v-toolbar-title class="text-subtitle-2">{{ $t('guiasMotorista.stopsTitle') }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon @click="stopsDrawerOpen = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <div class="guia-stops-drawer-body stops-scroll">
            <v-list two-line dense class="py-0">
              <v-list-item
                v-for="(p, idx) in paradasFiltradas"
                :key="`stop-drawer-${idx}`"
                style="cursor: pointer;"
                @click="focusStopFromDrawer(p)"
              >
                <v-list-item-content>
                  <v-list-item-title style="font-size: 15px;">
                    {{ p.cod_parada != null ? p.cod_parada : (idx + 1) }}
                    <span v-if="p.descricao_sentido"> · {{ p.descricao_sentido }}</span>
                  </v-list-item-title>
                  <v-list-item-subtitle style="font-size: 15px; overflow: break-word; white-space: normal;">
                    {{ p.endereco_parada }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-navigation-drawer>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogNovaGuia" fullscreen :retain-focus="false">
      <v-card class="nova-guia-card d-flex flex-column">
        <v-toolbar dense flat color="primary" dark class="nova-guia-toolbar">
          <v-btn icon @click="closeNovaGuiaCadastro">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title class="text-subtitle-1 font-weight-medium">
            {{ $t('guiasMotorista.newGuideTitle') }}
          </v-toolbar-title>
        </v-toolbar>
        <v-progress-linear v-if="cadastroFormDataLoading" indeterminate color="primary" />
        <v-card-text class="pa-4 nova-guia-body">
          <p class="text-body-1 text--secondary mb-6">{{ $t('guiasMotorista.newGuideSubtitle') }}</p>
          <v-form ref="formNovaGuia" v-model="novaGuiaFormValido">
            <v-autocomplete
              v-model="editedGuiaItem.id_linha"
              :items="linhasCadastro"
              item-text="nome_linha"
              item-value="id_linha"
              auto
              :label="$t('guide.selectLine')"
              :rules="[(v) => !!v || $t('guide.validation.lineRequired')]"
              outlined
              dense
              class="mb-4"
              :disabled="cadastroFormDataLoading"
            />
            <v-autocomplete
              v-model="editedGuiaItem.id_veiculo"
              :items="veiculosCadastro"
              item-text="veiculoLabel"
              item-value="id_veiculo"
              :label="$t('guide.selectVehicle')"
              :rules="[(v) => !!v || $t('guide.validation.vehicleRequired')]"
              outlined
              dense
              :disabled="cadastroFormDataLoading"
            />
          </v-form>
        </v-card-text>
        <div class="nova-guia-actions">
          <v-btn
            block
            depressed
            color="primary"
            dark
            class="nova-guia-submit-btn"
            :loading="cadastroSaving"
            :disabled="cadastroFormDataLoading"
            @click="saveNovaGuiaCadastro"
          >
            <v-icon left small>mdi-play-circle-outline</v-icon>
            {{ $t('guiasMotorista.newGuideOpen') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogMapaNavegacao" fullscreen :retain-focus="false">
      <v-card class="mapa-navegacao-card d-flex flex-column">
        <v-toolbar dense flat color="primary" dark class="mapa-navegacao-toolbar">
          <v-btn icon :title="$t('button.close')" @click="closeMotoristaNavigationMap">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title class="text-subtitle-1 font-weight-medium text-truncate">
            {{ navigationActive ? (guiaDetalhe && guiaDetalhe.nome_linha) || $t('guiasMotorista.navigationMapTitle') : $t('guiasMotorista.navigationMapTitle') }}
          </v-toolbar-title>
          <v-spacer />
          <v-menu v-if="!modoSomenteLeitura" offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" :title="$t('guiasMotorista.navExternalApps')">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="openExternalNavigation('google')">
                <v-list-item-icon><v-icon small>mdi-google-maps</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('guiasMotorista.openInGoogleMaps') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openExternalNavigation('waze')">
                <v-list-item-icon><v-icon small>mdi-waze</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('guiasMotorista.openInWaze') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn
            v-if="!modoSomenteLeitura"
            color="white"
            class="ml-1 primary--text"
            small
            depressed
            @click="toggleNavigationActive"
          >
            <v-icon left small>{{ navigationActive ? 'mdi-stop' : 'mdi-navigation' }}</v-icon>
            {{ navigationActive ? $t('guiasMotorista.stopNavigation') : $t('guiasMotorista.startNavigation') }}
          </v-btn>
        </v-toolbar>
        <div v-if="dialogMapaNavegacao" class="mapa-navegacao-body flex-grow-1">
          <mapa-motorista
            :custom-route="motoristaGoogleNavPath"
            :stops="paradasFiltradas"
            :navigation-active="navigationActive"
            :vehicle-position="guiaVeiculoPosicao"
            :default-center="centerDefault"
            map-height="calc(100vh - 48px)"
          />
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogCercaConfirm" persistent max-width="420">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-medium">
          {{ cercaConfirmTitle }}
        </v-card-title>
        <v-card-text class="text-body-2">
          {{ cercaConfirmMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="cercaConfirmSalvando" @click="cancelCercaConfirm">
            {{ $t('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="cercaConfirmSalvando"
            :disabled="cercaConfirmSalvando"
            @click="confirmCercaAction"
          >
            {{ $t('button.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogHistorico" fullscreen :retain-focus="false">
      <v-card class="historico-card d-flex flex-column">
        <v-toolbar dense flat color="primary" dark class="historico-toolbar">
          <v-btn icon :title="$t('button.close')" @click="closeHistorico">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title class="text-subtitle-1 font-weight-medium">
            {{ $t('guiasMotorista.historyTitle') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon :title="$t('guide.reload')" :loading="historicoLoading" @click="loadHistorico">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
        <v-progress-linear v-if="historicoLoading" indeterminate color="primary" />
        <v-card-text class="pa-0 historico-body">
          <v-list v-if="!historicoLoading && historicoItems.length === 0" two-line class="transparent">
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>{{ $t('guiasMotorista.historyEmpty') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-list v-else two-line class="py-0">
            <v-list-item v-for="item in historicoItems" :key="item.id_historico">
              <v-list-item-icon>
                <v-icon :color="historyActionColor(item.cod_tipo_acao)">
                  {{ historyActionIcon(item.cod_tipo_acao) }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ historyActionLabel(item.cod_tipo_acao) }}
                  <v-chip
                    v-if="item.confirmacao_fora_cerca"
                    x-small
                    color="orange"
                    text-color="white"
                    class="ml-1"
                  >
                    {{ $t('guiasMotorista.historyOutsideFence') }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.dt_acao_extenso || '—' }}
                  <span v-if="item.nome_cerca"> · {{ item.nome_cerca }}</span>
                  <span v-if="item.nome_linha"> · {{ item.nome_linha }}</span>
                  <span v-if="item.cod_veiculo"> · {{ item.cod_veiculo }}</span>
                  <span v-if="item.id_guia"> · #{{ item.id_guia }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import apiController from '@/utils/apiController';
import axios from 'axios';
import { ApiBaseUrl } from '@/constants';
import MapaMotorista from '@/components/mapas/MapaMotorista.vue';
import DateUtils from '@/utils/dateutils';
import EventBus from '@/utils/eventBus';
import translateDbField from '@/utils/translateDbField';
import { LMap, LTileLayer, LGeoJson, LMarker, LTooltip } from 'vue2-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export default {
  name: 'GuiasMotorista',
  inject: {
    motoristaJornadaState: { default: null },
  },
  components: {
    LMap,
    LTileLayer,
    LGeoJson,
    LMarker,
    LTooltip,
    MapaMotorista,
  },
  data() {
    return {
      token: null,
      idFuncionario: null,
      guiasRaw: [],
      showAllTripsToday: false,
      loading: false,

      dialogDetalhe: false,
      stopsDrawerOpen: false,
      loadingDetalhe: false,
      guiaDetalhe: null,
      rotasDetalhe: [],
      paradasDetalhe: [],
      sentidoSelecionado: 1,
      checkSalvando: false,
      checkWaitTick: 0,
      checkWaitTimerId: null,

      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      tileAttribution: '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      mapOptions: {
        zoomSnap: 0.5,
      },

      dialogMapaNavegacao: false,
      navigationActive: false,
      guiaVeiculoPosicao: null,
      veiculoPosPollTimerId: null,

      dialogNovaGuia: false,
      cadastroFormDataLoading: false,
      cadastroSaving: false,
      novaGuiaFormValido: true,
      idUsuarioCadastro: null,
      empresasCadastro: [],
      linhasCadastro: [],
      veiculosCadastro: [],
      funcionariosCadastro: [],
      statusCadastro: [],
      turnosCadastro: [],
      horaPegadaProgramada: null,

      dialogCercaConfirm: false,
      cercaConfirmAction: null,
      cercaConfirmCoords: null,
      cercaConfirmSalvando: false,
      cercaConfirmPayload: null,

      dialogHistorico: false,
      historicoItems: [],
      historicoLoading: false,

      editedGuiaItem: {
        id_linha: null,
        id_empresa: null,
        id_guia: null,
        id_rota: null,
        id_veiculo: null,
        status: null,
        status_extenso: null,
        hora_pegada: null,
        hora_largada: null,
        data_hora_pegada: null,
        data_hora_largada: null,
        turno: null,
        id_motorista: null,
        id_cobrador: null,
        ativa: null,
        alteracao_manual: null,
        dt_alteracao_manual: null,
        id_usuario_alteracao_manual: null,
        guia_original: null,
        ponto_partida: null,
        ponto_chegada: null,
        id_viagem: null,
        escalonamento: null,
        roleta_inicial: null,
        roleta_final: null,
      },
      sessionUser: null,
    };
  },
  computed: {
    inicioJornadaObrigatorio() {
      const getter = this.$store && this.$store.getters
        ? this.$store.getters['config/motoristaInicioJornadaObrigatorio']
        : true;
      return getter !== false;
    },
    jornadaAtiva() {
      return this.motoristaJornadaState ? this.motoristaJornadaState.jornadaAtiva : null;
    },
    modoSomenteLeitura() {
      if (!this.inicioJornadaObrigatorio) return false;
      return !this.jornadaAtiva;
    },
    motoristaCheckoutMinutosAposCheckin() {
      const raw =
        this.$store && this.$store.getters
          ? this.$store.getters['config/motoristaCheckoutMinutosAposCheckin']
          : null;
      if (raw == null || raw === '') return 5;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) return 0;
      return Math.floor(n);
    },
    motoristaCheckoutWaitMs() {
      const min = this.motoristaCheckoutMinutosAposCheckin;
      if (min <= 0) return 0;
      return min * 60 * 1000;
    },
    guias() {
      const all = Array.isArray(this.guiasRaw) ? this.guiasRaw : [];
      if (this.showAllTripsToday) return all;
      return all.filter((g) => g && this.isGuiaAtiva(g));
    },
    guiaMapCenter() {
      return this.centerDefault || [0, 0];
    },
    centerDefault() {
      const c = this.$store && this.$store.getters ? this.$store.getters['config/centerDefault'] : null;
      if (!c || !Array.isArray(c) || c.length < 2) return null;
      const lat = Number(c[0]);
      const lng = Number(c[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return [lat, lng];
    },
    rotasFiltradas() {
      const s = this.sentidoSelecionado;
      return (this.rotasDetalhe || []).filter((r) => r && r.id_sentido != null && Number(r.id_sentido) === Number(s));
    },
    paradasFiltradas() {
      const s = Number(this.sentidoSelecionado);
      const all = (this.paradasDetalhe || []).filter(Boolean);
      const specificIds = new Set(
        all
          .filter((p) => p.id_sentido != null && Number(p.id_sentido) === s && p.id_parada != null)
          .map((p) => String(p.id_parada)),
      );
      return all.filter((p) => {
        if (p.id_sentido == null) return true;
        const id = Number(p.id_sentido);
        if (id === s) return true;
        if (id === -1) {
          return p.id_parada == null || !specificIds.has(String(p.id_parada));
        }
        return false;
      });
    },
    motoristaTripCheckPhase() {
      void this.checkWaitTick;
      void this.motoristaCheckoutMinutosAposCheckin;
      const g = this.guiaDetalhe;
      if (!g) return 'idle';
      if (this.isGuiaEncerrada(g)) return 'done';
      const temOut =
        this.hasMotoristaTimestampField(g.dt_checkout_motorista) ||
        this.hasMotoristaTimestampField(g.dt_checkout_motorista_fmt);
      if (temOut) return 'done';
      const temIn =
        this.hasMotoristaTimestampField(g.dt_checkin_motorista) ||
        this.hasMotoristaTimestampField(g.dt_checkin_motorista_fmt);
      if (!temIn) {
        if (this.findGuiaComCheckinAtivo(g.id_guia)) return 'checkin_blocked';
        return 'checkin';
      }
      const ms = this.getCheckinTimestampMs(g);
      const minWaitMs = this.motoristaCheckoutWaitMs;
      if (minWaitMs > 0 && ms != null && Date.now() < ms + minWaitMs) return 'wait';
      return 'checkout';
    },
    checkinBlockedMessage() {
      const other = this.findGuiaComCheckinAtivo(
        this.guiaDetalhe && this.guiaDetalhe.id_guia != null ? this.guiaDetalhe.id_guia : null,
      );
      if (!other) return '';
      const label = other.descricao_curta || other.guia_original || other.id_guia;
      return this.$t('guiasMotorista.checkinBlockedOtherActive', { guia: label });
    },
    motoristaTripCheckButtonDisabled() {
      if (!this.guiaDetalhe || this.loadingDetalhe || this.checkSalvando) return true;
      const ph = this.motoristaTripCheckPhase;
      return ph === 'wait' || ph === 'idle' || ph === 'checkin_blocked';
    },
    motoristaTripCheckButtonVisible() {
      if (this.modoSomenteLeitura) return false;
      const ph = this.motoristaTripCheckPhase;
      return ph !== 'done' && ph !== 'checkin_blocked';
    },
    motoristaTripCheckButtonLabel() {
      const ph = this.motoristaTripCheckPhase;
      if (ph === 'checkin') return this.$t('guiasMotorista.checkin');
      if (ph === 'checkout') return this.$t('guiasMotorista.checkout');
      if (ph === 'wait') {
        const g = this.guiaDetalhe;
        const ms = g ? this.getCheckinTimestampMs(g) : null;
        const minWaitMs = this.motoristaCheckoutWaitMs;
        if (minWaitMs <= 0 || ms == null) return this.$t('guiasMotorista.tripCheckWait');
        const restMin = Math.max(1, Math.ceil((ms + minWaitMs - Date.now()) / 60000));
        return this.$t('guiasMotorista.tripCheckWaitMinutes', { n: restMin });
      }
      if (ph === 'done') return this.$t('guiasMotorista.tripCheckDone');
      return '';
    },
    motoristaTripCheckButtonIcon() {
      const ph = this.motoristaTripCheckPhase;
      if (ph === 'checkin') return 'mdi-map-marker-check';
      if (ph === 'checkout') return 'mdi-flag-checkered';
      if (ph === 'wait') return 'mdi-timer-sand';
      if (ph === 'done') return 'mdi-check-all';
      return 'mdi-help-circle-outline';
    },
    motoristaGoogleNavPath() {
      const out = [];
      (this.rotasFiltradas || []).forEach((r) => {
        if (r && r.geojson) this.appendGeojsonToGooglePath(r.geojson, out);
      });
      if (out.length) return out;
      (this.paradasFiltradas || []).forEach((p) => {
        const lat = Number(p.lat_parada);
        const lng = Number(p.lng_parada);
        if (Number.isFinite(lat) && Number.isFinite(lng)) out.push({ lat, lng });
      });
      if (out.length) return out;
      const c = this.centerDefault;
      if (c && c.length >= 2) {
        const lat = Number(c[0]);
        const lng = Number(c[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return [{ lat, lng }];
      }
      return [];
    },
    motoristaNavigationMapButtonVisible() {
      return (
        !this.loadingDetalhe &&
        Boolean(this.guiaDetalhe) &&
        this.motoristaGoogleNavPath.length > 0
      );
    },
    guiaDetalheWideLayout() {
      return Boolean(this.$vuetify && this.$vuetify.breakpoint.mdAndUp);
    },
    stopsDrawerWidth() {
      if (!this.$vuetify) return 360;
      const w = this.$vuetify.breakpoint.width;
      return Math.min(380, Math.max(280, Math.round(w * 0.88)));
    },
    cercaConfirmTitle() {
      if (this.cercaConfirmAction === 'nova_guia') {
        return this.$t('guiasMotorista.cercaConfirmNewGuideTitle');
      }
      return this.$t('guiasMotorista.cercaConfirmCheckinTitle');
    },
    cercaConfirmMessage() {
      if (this.cercaConfirmAction === 'nova_guia') {
        return this.$t('guiasMotorista.cercaConfirmNewGuideMessage');
      }
      return this.$t('guiasMotorista.cercaConfirmCheckinMessage');
    },
  },
  watch: {
    modoSomenteLeitura(readonly) {
      if (readonly && this.navigationActive) {
        this.navigationActive = false;
      }
    },
    centerDefault() {
      if (!this.dialogDetalhe && !this.dialogMapaNavegacao) return;
      this.$nextTick(() => this.fitGuiaMap());
    },
    sentidoSelecionado() {
      this.$nextTick(() => this.fitGuiaMap());
    },
    guiaDetalheWideLayout(wide) {
      if (wide) this.stopsDrawerOpen = false;
      this.$nextTick(() => this.fitGuiaMap());
    },
    stopsDrawerOpen() {
      this.$nextTick(() => this.fitGuiaMap());
    },
    motoristaCheckoutMinutosAposCheckin() {
      if (this.dialogDetalhe && this.guiaDetalhe) {
        this.$nextTick(() => this.maybeStartCheckWaitTicker());
      }
    },
  },
  beforeDestroy() {
    EventBus.$off('guias-motorista-nav-nova-viagem', this.onNavBarNovaViagemMotorista);
    EventBus.$off('motorista-jornada-updated', this.onMotoristaJornadaUpdated);
    this.clearCheckWaitTicker();
    this.stopVeiculoPosPoll();
  },
  mounted() {
    EventBus.$on('guias-motorista-nav-nova-viagem', this.onNavBarNovaViagemMotorista);
    EventBus.$on('motorista-jornada-updated', this.onMotoristaJornadaUpdated);
  },
  created() {
    const raw = sessionStorage.getItem('user');
    if (raw) {
      try {
        const u = JSON.parse(raw);
        this.idFuncionario = u.id_funcionario != null ? u.id_funcionario : null;
        this.sessionUser = u;
      } catch (e) {
        this.idFuncionario = null;
        this.sessionUser = null;
      }
    }
    this.token = sessionStorage.getItem('api');
    this.ensureMotoristaConfig().finally(() => {
      this.loadGuias();
    });
  },
  methods: {
    ensureMotoristaConfig() {
      if (!this.token) return Promise.resolve();
      return axios
        .get(`${ApiBaseUrl}/api/v1.0/getallconfig`, {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          const cfg = response.data && response.data[0];
          if (cfg) this.$store.commit('config/setConfig', cfg);
        })
        .catch(() => {});
    },
    resetNovaGuiaFormState() {
      this.editedGuiaItem = {
        id_linha: null,
        id_empresa: null,
        id_guia: null,
        id_rota: null,
        id_veiculo: null,
        status: null,
        status_extenso: null,
        hora_pegada: null,
        hora_largada: null,
        data_hora_pegada: null,
        data_hora_largada: null,
        turno: null,
        id_motorista: null,
        id_cobrador: null,
        ativa: null,
        alteracao_manual: null,
        dt_alteracao_manual: null,
        id_usuario_alteracao_manual: null,
        guia_original: null,
        ponto_partida: null,
        ponto_chegada: null,
        id_viagem: null,
        escalonamento: null,
        roleta_inicial: null,
        roleta_final: null,
      };
      this.horaPegadaProgramada = new Date();
      this.$nextTick(() => {
        if (this.$refs.formNovaGuia) this.$refs.formNovaGuia.resetValidation();
      });
    },
    closeNovaGuiaCadastro() {
      this.dialogNovaGuia = false;
      this.resetNovaGuiaFormState();
    },
    onNavBarNovaViagemMotorista() {
      if (this.modoSomenteLeitura) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('guiasMotorista.viewOnlyWithoutJornada'),
          tipo: 'warning',
        });
        return;
      }
      if (!this.idFuncionario) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('guiasMotorista.noDriverLinked'),
          tipo: 'warning',
        });
        return;
      }
      if (!this.token) return;
      this.openNovaGuiaCadastro();
    },
    async openNovaGuiaCadastro() {
      if (!this.token || this.modoSomenteLeitura) return;
      this.resetNovaGuiaFormState();
      this.dialogNovaGuia = true;
      this.cadastroFormDataLoading = true;
      try {
        const res = await apiController.request(apiController.endpoints.getGuiamotoristainsertformdata, {
          silent: true,
        });
        const d = res && res.data ? res.data : {};
        this.idUsuarioCadastro = d.id_usuario != null ? d.id_usuario : this.sessionUser && this.sessionUser.id_usuario;
        this.empresasCadastro = Array.isArray(d.empresas) ? d.empresas : [];
        this.linhasCadastro = Array.isArray(d.linhas) ? d.linhas : [];
        this.veiculosCadastro = (Array.isArray(d.veiculos) ? d.veiculos : []).map((v) => ({
          ...v,
          veiculoLabel: v.placa ? `${v.numero_ordem} · ${v.placa}` : String(v.numero_ordem),
        }));
        this.funcionariosCadastro = Array.isArray(d.funcionarios) ? d.funcionarios : [];
        this.turnosCadastro = Array.isArray(d.turnos) ? d.turnos : [];
        this.statusCadastro = (Array.isArray(d.status) ? d.status : []).map((status) => ({
          ...status,
          descricao_status_guia: translateDbField(status.translation_key, status.descricao_status_guia),
        }));

        const u = this.sessionUser;
        if (u && u.id_empresa != null) {
          this.editedGuiaItem.id_empresa = u.id_empresa;
        } else if (this.empresasCadastro.length === 1) {
          this.editedGuiaItem.id_empresa = this.empresasCadastro[0].id_empresa;
        }
        if (this.idFuncionario != null) {
          this.editedGuiaItem.id_motorista = this.idFuncionario;
        }
        if (this.turnosCadastro.length) {
          this.editedGuiaItem.turno = this.turnosCadastro[0].id_turno;
        }
        if (this.statusCadastro.length) {
          this.editedGuiaItem.status = this.statusCadastro[0].id_status_guia;
        }
        this.editedGuiaItem.escalonamento = 1;
        this.applyNovaGuiaCadastroDefaults();
      } catch (e) {
        const msg =
          e.response && e.response.data && e.response.data.mensagem
            ? e.response.data.mensagem
            : this.$t('guiasMotorista.newGuideLoadFormError');
        EventBus.$emit('show-alert', { mensagem: msg, tipo: 'error' });
        this.dialogNovaGuia = false;
      } finally {
        this.cadastroFormDataLoading = false;
      }
    },
    applyNovaGuiaCadastroDefaults() {
      const last = this.getLastGuiaTodayForPrefill();
      if (last) {
        if (
          last.id_linha != null &&
          this.linhasCadastro.some((l) => String(l.id_linha) === String(last.id_linha))
        ) {
          this.editedGuiaItem.id_linha = last.id_linha;
        }
        if (
          last.id_veiculo != null &&
          this.veiculosCadastro.some((v) => String(v.id_veiculo) === String(last.id_veiculo))
        ) {
          this.editedGuiaItem.id_veiculo = last.id_veiculo;
        }
      }
      if (!this.editedGuiaItem.id_linha && this.linhasCadastro.length === 1) {
        this.editedGuiaItem.id_linha = this.linhasCadastro[0].id_linha;
      }
      if (!this.editedGuiaItem.id_veiculo && this.veiculosCadastro.length === 1) {
        this.editedGuiaItem.id_veiculo = this.veiculosCadastro[0].id_veiculo;
      }
    },
    getLastGuiaTodayForPrefill() {
      const list = Array.isArray(this.guiasRaw) ? this.guiasRaw : [];
      if (!list.length) return null;
      return [...list].sort((a, b) => {
        const msA = this.parseDateTimeToMs(a.data_hora_pegada || a.dt_pegada) || 0;
        const msB = this.parseDateTimeToMs(b.data_hora_pegada || b.dt_pegada) || 0;
        return msB - msA;
      })[0];
    },
    async saveNovaGuiaCadastro() {
      if (this.modoSomenteLeitura) return;
      if (!this.$refs.formNovaGuia || !this.token) return;
      const ok = this.$refs.formNovaGuia.validate();
      if (!ok) return;
      const idUsuario =
        this.idUsuarioCadastro != null
          ? this.idUsuarioCadastro
          : this.sessionUser && this.sessionUser.id_usuario != null
            ? this.sessionUser.id_usuario
            : null;
      if (idUsuario == null) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('guiasMotorista.newGuideNoUserId'),
          tipo: 'warning',
        });
        return;
      }

      const payload = { ...this.editedGuiaItem };
      payload.hora_pegada = DateUtils.getDateSQLFormatted(new Date());
      payload.hora_largada = null;
      payload.guia_original = null;
      payload.alteracao_manual = 1;
      payload.dt_alteracao_manual = DateUtils.getDateSQLFormatted(new Date());
      payload.id_usuario_alteracao_manual = idUsuario;

      const veiculo = this.veiculosCadastro.find((v) => String(v.id_veiculo) === String(payload.id_veiculo));
      if (veiculo) {
        payload.cod_veiculo = veiculo.numero_ordem;
        payload.placa = veiculo.placa;
      }

      const linha = this.linhasCadastro.find((l) => String(l.id_linha) === String(payload.id_linha));
      if (linha) {
        payload.cod_linha = linha.cod_linha;
        payload.cod_linha_guia = linha.cod_integracao_linha;
        payload.nome_linha = linha.nome_linha;
        payload.velocidade_maxima = linha.velocidade_maxima;
        payload.alarme_velocidade_maxima = linha.alarme_velocidade_maxima;
        payload.alarme_fora_rota = linha.alarme_fora_rota;
        payload.circular = linha.circular;
        payload.alarme_tempo_parado = linha.alarme_tempo_parado;
        payload.limite_tempo_parado = linha.limite_tempo_parado;
      }

      const motorista = this.funcionariosCadastro.find((f) => String(f.id_funcionario) === String(payload.id_motorista));
      if (motorista) {
        payload.id_motorista = motorista.id_funcionario;
        payload.nome_motorista = motorista.nome;
        payload.matricula_motorista = motorista.matricula;
        payload.cod_motorista_guia = motorista.matricula;
      }

      if (payload.id_cobrador) {
        const cobrador = this.funcionariosCadastro.find((f) => String(f.id_funcionario) === String(payload.id_cobrador));
        if (cobrador) {
          payload.id_cobrador = cobrador.id_funcionario;
          payload.nome_cobrador = cobrador.nome;
          payload.matricula_cobrador = cobrador.matricula;
          payload.cod_cobrador_guia = cobrador.matricula;
        }
      }

      const empresa = this.empresasCadastro.find((e) => String(e.id_empresa) === String(payload.id_empresa));
      if (empresa) {
        payload.razao_social = empresa.razao_social;
      }

      const st = this.statusCadastro.find((s) => String(s.id_status_guia) === String(payload.status));
      if (st) {
        payload.status_extenso = st.descricao_status_guia;
      }

      payload.data_abertura_erp = DateUtils.getDateSQLFormatted(new Date());
      payload.ativa = true;

      this.cadastroSaving = true;
      try {
        await this.submitNovaGuiaCadastro(payload);
      } finally {
        this.cadastroSaving = false;
      }
    },
    async submitNovaGuiaCadastro(payload, options = {}) {
      try {
        const body = { ...payload };
        const coords = options.coords || (await this.getCurrentPosition());
        body.lat = coords.lat;
        body.lng = coords.lng;
        if (options.confirmar_fora_cerca) {
          body.confirmar_fora_cerca = 1;
        }
        await axios.post(`${ApiBaseUrl}/api/v1.0/postguiamotoristainsertguiacadastro`, body, {
          headers: { 'x-access-token': this.token },
        });
        EventBus.$emit('show-alert', { mensagem: this.$t('guiasMotorista.newGuideSuccess'), tipo: 'success' });
        this.closeNovaGuiaCadastro();
        this.loadGuias();
        this.refreshHistoricoIfOpen();
        return true;
      } catch (e) {
        if (!e || !e.response) {
          EventBus.$emit('show-alert', {
            mensagem: this.$t('guiasMotorista.cercaLocationError'),
            tipo: 'warning',
          });
          return false;
        }
        const data = e.response.data;
        if (data && data.codigo === 'fora_cerca_confirmar') {
          if (options.confirmar_fora_cerca) {
            EventBus.$emit('show-alert', {
              mensagem: data.mensagem || this.$t('guiasMotorista.newGuideSaveError'),
              tipo: 'error',
            });
            return false;
          }
          let coords = options.coords;
          if (!coords) {
            try {
              coords = await this.getCurrentPosition();
            } catch (geoErr) {
              EventBus.$emit('show-alert', {
                mensagem: this.$t('guiasMotorista.cercaLocationError'),
                tipo: 'warning',
              });
              return false;
            }
          }
          this.cercaConfirmCoords = coords;
          this.cercaConfirmPayload = payload;
          this.cercaConfirmAction = 'nova_guia';
          this.dialogCercaConfirm = true;
          return false;
        }
        let msg =
          data && data.mensagem ? data.mensagem : this.$t('guiasMotorista.newGuideSaveError');
        if (data && data.codigo === 'fora_cerca') {
          msg = this.$t('guiasMotorista.cercaBlockedMessage');
        }
        EventBus.$emit('show-alert', { mensagem: msg, tipo: 'error' });
        return false;
      }
    },
    formatLocalDateTimeForApi(date) {
      if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return null;
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      const s = String(date.getSeconds()).padStart(2, '0');
      return `${y}-${m}-${d} ${h}:${min}:${s}`;
    },
    getDtLargadaDisplay(g) {
      if (!g) return null;
      if (g.dt_largada) return g.dt_largada;
      const largada = g.data_hora_largada || g.data_hora_largada_motorista;
      if (!largada) return null;
      const ms = this.parseDateTimeToMs(largada);
      if (ms == null) return String(largada);
      const d = new Date(ms);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hour = String(d.getHours()).padStart(2, '0');
      const minute = String(d.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hour}:${minute}`;
    },
    parseDateTimeToMs(dt) {
      if (dt == null || dt === '') return null;
      if (dt instanceof Date) {
        const ms = dt.getTime();
        return Number.isFinite(ms) ? ms : null;
      }
      if (typeof dt === 'number') return Number.isFinite(dt) ? dt : null;

      const raw = String(dt).trim();
      if (!raw) return null;

      // Try native parse first (works for ISO: YYYY-MM-DDTHH:mm:ss)
      const msNative = new Date(raw).getTime();
      if (Number.isFinite(msNative)) return msNative;

      // MySQL-ish "YYYY-MM-DD HH:mm:ss" (avoid relying on browser-specific parsing)
      const mIsoSpace = raw.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (mIsoSpace) {
        const year = Number(mIsoSpace[1]);
        const month = Number(mIsoSpace[2]) - 1;
        const day = Number(mIsoSpace[3]);
        const hour = Number(mIsoSpace[4]);
        const minute = Number(mIsoSpace[5]);
        const second = mIsoSpace[6] != null ? Number(mIsoSpace[6]) : 0;
        const d = new Date(year, month, day, hour, minute, second, 0);
        const ms = d.getTime();
        return Number.isFinite(ms) ? ms : null;
      }

      // API commonly returns "DD/MM/YYYY HH:mm" (no seconds). DateUtils.getDateSQLFormatted assumes ":ss".
      const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (m) {
        const day = Number(m[1]);
        const month = Number(m[2]) - 1;
        const year = Number(m[3]);
        const hour = Number(m[4]);
        const minute = Number(m[5]);
        const second = m[6] != null ? Number(m[6]) : 0;
        const d = new Date(year, month, day, hour, minute, second, 0);
        const ms = d.getTime();
        return Number.isFinite(ms) ? ms : null;
      }

      // Fallback: convert other pt-BR-ish strings into ISO-ish and parse
      try {
        const iso = DateUtils.getDateSQLFormatted(raw);
        const ms2 = new Date(iso).getTime();
        return Number.isFinite(ms2) ? ms2 : null;
      } catch (e) {
        return null;
      }
    },
    todayRangeSql() {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      return {
        dti: DateUtils.getDateSQLFormatted(start),
        dtf: DateUtils.getDateSQLFormatted(end),
      };
    },
    openHistorico() {
      this.dialogHistorico = true;
      this.loadHistorico();
    },
    closeHistorico() {
      this.dialogHistorico = false;
    },
    refreshHistoricoIfOpen() {
      if (this.dialogHistorico) {
        this.loadHistorico();
      }
    },
    loadHistorico() {
      if (!this.token || !this.idFuncionario) {
        this.historicoItems = [];
        return Promise.resolve();
      }
      const { dti, dtf } = this.todayRangeSql();
      this.historicoLoading = true;
      return axios
        .get(`${ApiBaseUrl}/api/v1.0/getguiamotoristahistorico/${dti}/${dtf}`, {
          headers: { 'x-access-token': this.token },
        })
        .then((res) => {
          const payload = res && res.data ? res.data : {};
          this.historicoItems = Array.isArray(payload.historico) ? payload.historico : [];
        })
        .catch(() => {
          this.historicoItems = [];
          EventBus.$emit('show-alert', {
            mensagem: this.$t('guiasMotorista.historyLoadError'),
            tipo: 'error',
          });
        })
        .finally(() => {
          this.historicoLoading = false;
        });
    },
    historyActionLabel(codTipoAcao) {
      const map = {
        CHECKIN: this.$t('guiasMotorista.historyActionCheckin'),
        CHECKOUT: this.$t('guiasMotorista.historyActionCheckout'),
        NOVA_GUIA: this.$t('guiasMotorista.historyActionNewGuide'),
      };
      return map[codTipoAcao] || codTipoAcao || '—';
    },
    historyActionIcon(codTipoAcao) {
      const map = {
        CHECKIN: 'mdi-map-marker-check',
        CHECKOUT: 'mdi-flag-checkered',
        NOVA_GUIA: 'mdi-play-circle-outline',
      };
      return map[codTipoAcao] || 'mdi-history';
    },
    historyActionColor(codTipoAcao) {
      const map = {
        CHECKIN: 'success',
        CHECKOUT: 'primary',
        NOVA_GUIA: 'orange darken-2',
      };
      return map[codTipoAcao] || 'grey';
    },
    loadGuias(options = {}) {
      if (!this.token || !this.idFuncionario) {
        this.guiasRaw = [];
        return Promise.resolve();
      }
      const { dti, dtf } = this.todayRangeSql();
      this.loading = true;
      return axios
        .get(`${ApiBaseUrl}/api/v1.0/getguiasmotoristadia/${dti}/${dtf}`, {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          this.guiasRaw = Array.isArray(response.data) ? response.data : [];
          const syncDetalheId = options.syncDetalheId;
          if (syncDetalheId != null && this.guiaDetalhe) {
            const updated = this.guiasRaw.find(
              (g) => g && String(g.id_guia) === String(syncDetalheId),
            );
            if (updated) {
              this.guiaDetalhe = { ...this.guiaDetalhe, ...updated };
              this.clearCheckWaitTicker();
            }
          }
        })
        .catch((e) => {
          this.guiasRaw = [];
          const msg =
            e.response && e.response.data && e.response.data.mensagem
              ? e.response.data.mensagem
              : this.$t('guiasMotorista.loadError');
          EventBus.$emit('show-alert', { mensagem: msg, tipo: 'error' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onMotoristaJornadaUpdated() {
      const detalheId = this.guiaDetalhe && this.guiaDetalhe.id_guia != null ? this.guiaDetalhe.id_guia : null;
      this.loadGuias({ syncDetalheId: detalheId });
    },
    closeGuiaDetalhe() {
      this.closeMotoristaNavigationMap();
      this.clearCheckWaitTicker();
      this.stopsDrawerOpen = false;
      this.dialogDetalhe = false;
      this.guiaDetalhe = null;
      this.rotasDetalhe = [];
      this.paradasDetalhe = [];
      this.sentidoSelecionado = 1;
      this.checkSalvando = false;
      this.checkWaitTick = 0;
    },
    clearCheckWaitTicker() {
      if (this.checkWaitTimerId != null) {
        clearInterval(this.checkWaitTimerId);
        this.checkWaitTimerId = null;
      }
    },
    getCheckinTimestampMs(g) {
      if (!g) return null;
      if (this.hasMotoristaTimestampField(g.dt_checkin_motorista)) {
        return this.parseDateTimeToMs(g.dt_checkin_motorista);
      }
      if (this.hasMotoristaTimestampField(g.dt_checkin_motorista_fmt)) {
        return this.parseDateTimeToMs(g.dt_checkin_motorista_fmt);
      }
      return null;
    },
    maybeStartCheckWaitTicker() {
      this.clearCheckWaitTicker();
      const g = this.guiaDetalhe;
      if (!g || this.isGuiaEncerrada(g)) return;
      const temOut =
        this.hasMotoristaTimestampField(g.dt_checkout_motorista) ||
        this.hasMotoristaTimestampField(g.dt_checkout_motorista_fmt);
      if (temOut) return;
      const temIn =
        this.hasMotoristaTimestampField(g.dt_checkin_motorista) ||
        this.hasMotoristaTimestampField(g.dt_checkin_motorista_fmt);
      if (!temIn) return;
      const ms = this.getCheckinTimestampMs(g);
      const minWaitMs = this.motoristaCheckoutWaitMs;
      if (minWaitMs <= 0) return;
      if (ms == null || Date.now() >= ms + minWaitMs) return;
      this.checkWaitTimerId = setInterval(() => {
        this.checkWaitTick += 1;
        const g2 = this.guiaDetalhe;
        const ms2 = g2 ? this.getCheckinTimestampMs(g2) : null;
        if (minWaitMs <= 0 || ms2 == null || Date.now() >= ms2 + minWaitMs) {
          this.clearCheckWaitTicker();
        }
      }, 10000);
    },
    hasMotoristaTimestampField(val) {
      if (val == null || val === '') return false;
      if (val instanceof Date) return !Number.isNaN(val.getTime());
      return String(val).trim() !== '';
    },
    isGuiaEncerrada(g) {
      if (!g) return false;
      if (g.ativa === 0 || g.ativa === '0' || g.ativa_extenso === 'Não') return true;
      if (g.status != null && g.status !== '' && Number(g.status) !== 1) return true;
      if (this.hasMotoristaTimestampField(g.data_fechamento_erp)) return true;
      const largadaMs = this.parseDateTimeToMs(
        g.data_hora_largada || g.data_hora_largada_motorista || g.dt_largada,
      );
      if (largadaMs != null && largadaMs <= Date.now()) return true;
      return false;
    },
    isGuiaAtiva(g) {
      if (!g) return false;
      if (g.ativa === 0 || g.ativa === '0' || g.ativa_extenso === 'Não') return false;
      if (g.ativa === 1 || g.ativa === '1' || g.ativa_extenso === 'Sim') return true;
      return true;
    },
    isGuiaComCheckinAtivo(g) {
      if (!g || this.isGuiaEncerrada(g)) return false;
      const temIn =
        this.hasMotoristaTimestampField(g.dt_checkin_motorista) ||
        this.hasMotoristaTimestampField(g.dt_checkin_motorista_fmt);
      const temOut =
        this.hasMotoristaTimestampField(g.dt_checkout_motorista) ||
        this.hasMotoristaTimestampField(g.dt_checkout_motorista_fmt);
      return temIn && !temOut;
    },
    findGuiaComCheckinAtivo(excludeId) {
      const list = Array.isArray(this.guiasRaw) ? this.guiasRaw : [];
      return (
        list.find((g) => {
          if (!g || !this.isGuiaComCheckinAtivo(g)) return false;
          if (excludeId != null && String(g.id_guia) === String(excludeId)) return false;
          return true;
        }) || null
      );
    },
    syncGuiaInList(guiaAtualizada) {
      if (!guiaAtualizada || guiaAtualizada.id_guia == null) return;
      const id = guiaAtualizada.id_guia;
      const raw = Array.isArray(this.guiasRaw) ? this.guiasRaw : [];
      this.guiasRaw = raw.map((g) => (g && g.id_guia == id ? { ...g, ...guiaAtualizada } : g));
    },
    async motoristaTripCheckTap() {
      if (this.modoSomenteLeitura) return;
      const ph = this.motoristaTripCheckPhase;
      if (ph === 'checkin') return this.motoristaCheck('checkin');
      if (ph === 'checkout') return this.motoristaCheck('checkout');
    },
    async motoristaCheck(action, options = {}) {
      if (!this.token || !this.guiaDetalhe || this.guiaDetalhe.id_guia == null) return;
      if (action === 'checkin') {
        const other = this.findGuiaComCheckinAtivo(this.guiaDetalhe.id_guia);
        if (other) {
          const label = other.descricao_curta || other.guia_original || other.id_guia;
          EventBus.$emit('show-alert', {
            mensagem: this.$t('guiasMotorista.checkinBlockedOtherActive', { guia: label }),
            tipo: 'warning',
          });
          return;
        }
      }
      if (action === 'checkout') {
        const ms = this.getCheckinTimestampMs(this.guiaDetalhe);
        const minWaitMs = this.motoristaCheckoutWaitMs;
        if (minWaitMs > 0 && ms != null && Date.now() < ms + minWaitMs) {
          const restMin = Math.max(1, Math.ceil((ms + minWaitMs - Date.now()) / 60000));
          EventBus.$emit('show-alert', {
            mensagem: this.$t('guiasMotorista.checkoutTooSoonMinutes', { n: restMin }),
            tipo: 'warning',
          });
          return;
        }
      }
      this.checkSalvando = true;
      let checkinCoords = null;
      try {
        const body = { id_guia: this.guiaDetalhe.id_guia, action };
        if (action === 'checkout') {
          body.data_hora_largada_motorista = this.formatLocalDateTimeForApi(new Date());
        }
        if (action === 'checkin') {
          checkinCoords = options.coords || null;
          if (!checkinCoords) {
            checkinCoords = await this.getCurrentPosition();
          }
          const confirmadoFora = !!options.confirmar_fora_cerca;
          if (!confirmadoFora) {
            const validateBody = this.buildCheckinBody(checkinCoords, { somente_validacao_cerca: true });
            try {
              await axios.post(
                `${ApiBaseUrl}/api/v1.0/postguiamotoristacheck`,
                validateBody,
                { headers: { 'x-access-token': this.token } },
              );
            } catch (validateErr) {
              return this.handleCheckinCercaError(validateErr, checkinCoords, options);
            }
          }
          Object.assign(body, this.buildCheckinBody(checkinCoords, { confirmar_fora_cerca: confirmadoFora }));
        }
        const res = await axios.post(
          `${ApiBaseUrl}/api/v1.0/postguiamotoristacheck`,
          body,
          { headers: { 'x-access-token': this.token } },
        );
        const payload = res && res.data ? res.data : {};
        if (payload.guia) {
          this.guiaDetalhe = { ...this.guiaDetalhe, ...payload.guia };
          this.syncGuiaInList(payload.guia);
        }
        const okMsg =
          action === 'checkin' ? this.$t('guiasMotorista.checkinSuccess') : this.$t('guiasMotorista.checkoutSuccess');
        EventBus.$emit('show-alert', { mensagem: okMsg, tipo: 'success' });
        if (action === 'checkin') {
          this.$nextTick(() => this.maybeStartCheckWaitTicker());
        } else {
          this.clearCheckWaitTicker();
        }
        this.refreshHistoricoIfOpen();
        return true;
      } catch (e) {
        if (action === 'checkin') {
          return this.handleCheckinCercaError(e, checkinCoords, options);
        }
        if (!e || !e.response) {
          return false;
        }
        const status = e.response.status;
        const data = e.response.data;
        let msg = data && data.mensagem ? data.mensagem : this.$t('guiasMotorista.checkError');
        EventBus.$emit('show-alert', { mensagem: msg, tipo: status === 409 ? 'warning' : 'error' });
        return false;
      } finally {
        this.checkSalvando = false;
      }
    },
    buildCheckinBody(coords, options = {}) {
      const body = {
        id_guia: this.guiaDetalhe.id_guia,
        action: 'checkin',
        lat: coords.lat,
        lng: coords.lng,
      };
      if (options.confirmar_fora_cerca) {
        body.confirmar_fora_cerca = 1;
      }
      if (options.somente_validacao_cerca) {
        body.somente_validacao_cerca = 1;
      }
      return body;
    },
    handleCheckinCercaError(e, checkinCoords, options = {}) {
      if (!e || !e.response) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('guiasMotorista.cercaLocationError'),
          tipo: 'warning',
        });
        return false;
      }
      const data = e.response.data;
      if (data && data.codigo === 'fora_cerca_confirmar') {
        if (options.confirmar_fora_cerca) {
          EventBus.$emit('show-alert', {
            mensagem: data.mensagem || this.$t('guiasMotorista.checkError'),
            tipo: 'error',
          });
          return false;
        }
        this.cercaConfirmCoords = checkinCoords || options.coords || null;
        if (!this.cercaConfirmCoords) {
          EventBus.$emit('show-alert', {
            mensagem: this.$t('guiasMotorista.cercaLocationError'),
            tipo: 'warning',
          });
          return false;
        }
        this.cercaConfirmAction = 'checkin';
        this.dialogCercaConfirm = true;
        return false;
      }
      let msg = data && data.mensagem ? data.mensagem : this.$t('guiasMotorista.checkError');
      if (data && data.codigo === 'fora_cerca') {
        msg = this.$t('guiasMotorista.cercaBlockedMessage');
      }
      const status = e.response.status;
      EventBus.$emit('show-alert', { mensagem: msg, tipo: status === 409 ? 'warning' : 'error' });
      return false;
    },
    getCurrentPosition() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('geolocation_unavailable'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (!pos || !pos.coords) {
              reject(new Error('position_unavailable'));
              return;
            }
            const lat = Number(pos.coords.latitude);
            const lng = Number(pos.coords.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
              reject(new Error('invalid_coords'));
              return;
            }
            resolve({ lat, lng });
          },
          (err) => reject(err || new Error('geolocation_error')),
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
        );
      });
    },
    resetCercaConfirmDialog() {
      this.dialogCercaConfirm = false;
      this.cercaConfirmAction = null;
      this.cercaConfirmCoords = null;
      this.cercaConfirmPayload = null;
    },
    cancelCercaConfirm() {
      if (this.cercaConfirmSalvando) return;
      this.resetCercaConfirmDialog();
    },
    async confirmCercaAction() {
      const action = this.cercaConfirmAction;
      const coords = this.cercaConfirmCoords;
      const payload = this.cercaConfirmPayload;
      if (!action) return;
      this.resetCercaConfirmDialog();
      this.cercaConfirmSalvando = true;
      try {
        if (action === 'checkin') {
          await this.motoristaCheck('checkin', {
            coords,
            confirmar_fora_cerca: true,
          });
          return;
        }
        if (action === 'nova_guia' && payload) {
          await this.submitNovaGuiaCadastro(payload, {
            coords,
            confirmar_fora_cerca: true,
          });
        }
      } finally {
        this.cercaConfirmSalvando = false;
      }
    },
    appendGeojsonToGooglePath(geo, out) {
      if (!geo || !Array.isArray(out)) return;
      try {
        if (geo.type === 'Feature' && geo.geometry) {
          this.appendGeojsonToGooglePath(geo.geometry, out);
          return;
        }
        if (geo.type === 'FeatureCollection' && Array.isArray(geo.features)) {
          geo.features.forEach((f) => this.appendGeojsonToGooglePath(f, out));
          return;
        }
        if (geo.type === 'LineString' && Array.isArray(geo.coordinates)) {
          geo.coordinates.forEach((c) => {
            if (!Array.isArray(c) || c.length < 2) return;
            const lng = Number(c[0]);
            const lat = Number(c[1]);
            if (Number.isFinite(lat) && Number.isFinite(lng)) out.push({ lat, lng });
          });
          return;
        }
        if (geo.type === 'MultiLineString' && Array.isArray(geo.coordinates)) {
          geo.coordinates.forEach((line) => {
            if (!Array.isArray(line)) return;
            line.forEach((c) => {
              if (!Array.isArray(c) || c.length < 2) return;
              const lng = Number(c[0]);
              const lat = Number(c[1]);
              if (Number.isFinite(lat) && Number.isFinite(lng)) out.push({ lat, lng });
            });
          });
        }
      } catch (e) {
        /* ignore */
      }
    },
    openMotoristaNavigationMap() {
      if (!this.motoristaGoogleNavPath.length) return;
      this.navigationActive = false;
      this.dialogMapaNavegacao = true;
      this.syncGuiaVeiculoPosicaoFromDetalhe();
      this.startVeiculoPosPoll();
    },
    closeMotoristaNavigationMap() {
      this.dialogMapaNavegacao = false;
      this.navigationActive = false;
      this.stopVeiculoPosPoll();
    },
    toggleNavigationActive() {
      if (this.modoSomenteLeitura) return;
      this.navigationActive = !this.navigationActive;
      if (this.navigationActive) {
        this.startVeiculoPosPoll();
      }
    },
    syncGuiaVeiculoPosicaoFromDetalhe() {
      const v = this.guiaDetalhe && this.guiaDetalhe.veiculo;
      if (!v) {
        this.guiaVeiculoPosicao = null;
        return;
      }
      const lat = Number(v.lat);
      const lng = Number(v.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
        this.guiaVeiculoPosicao = null;
        return;
      }
      this.guiaVeiculoPosicao = { lat, lng, gps_offline: v.gps_offline, rumo: v.rumo };
    },
    startVeiculoPosPoll() {
      this.stopVeiculoPosPoll();
      if (!this.dialogMapaNavegacao || !this.guiaDetalhe || !this.guiaDetalhe.id_guia) return;
      const poll = () => {
        if (!this.dialogMapaNavegacao || !this.token || !this.guiaDetalhe) return;
        apiController.request(apiController.endpoints.getGuiamotoristaveiculoposicao, {
          params: { id_guia: this.guiaDetalhe.id_guia },
          silent: true,
          onSuccess: (res) => {
            const v = res && res.data && res.data.veiculo;
            if (!v) return;
            const lat = Number(v.lat);
            const lng = Number(v.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              this.guiaVeiculoPosicao = { lat, lng, gps_offline: v.gps_offline, rumo: v.rumo };
            }
          },
        }).catch(() => {});
      };
      poll();
      this.veiculoPosPollTimerId = setInterval(poll, 9000);
    },
    stopVeiculoPosPoll() {
      if (this.veiculoPosPollTimerId) {
        clearInterval(this.veiculoPosPollTimerId);
        this.veiculoPosPollTimerId = null;
      }
    },
    openExternalNavigation(app) {
      if (this.modoSomenteLeitura) return;
      const path = this.motoristaGoogleNavPath;
      if (!path || !path.length) return;
      const dest = path[path.length - 1];
      const lat = Number(dest.lat);
      const lng = Number(dest.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      let url;
      if (app === 'waze') {
        url = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
      } else {
        const waypoints = (this.paradasFiltradas || [])
          .slice(0, 3)
          .map((p) => `${Number(p.lat_parada)},${Number(p.lng_parada)}`)
          .filter((s) => !s.includes('NaN'))
          .join('|');
        url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
        if (waypoints) url += `&waypoints=${encodeURIComponent(waypoints)}`;
      }
      window.open(url, '_blank');
    },
    rotaStyle() {
      return { color: 'blue', weight: 4, opacity: 0.9 };
    },
    paradaTooltip(p) {
      const cod = p && p.cod_parada != null ? String(p.cod_parada) : '';
      const addr = p && p.endereco_parada ? String(p.endereco_parada) : '';
      return [cod, addr].filter((x) => x && x.trim() !== '').join(' - ');
    },
    setSentido(idSentido) {
      const s = Number(idSentido);
      if (s !== 1 && s !== 2) return;
      if (this.sentidoSelecionado === s) return;
      this.sentidoSelecionado = s;
    },
    focusStop(p) {
      const lat = p ? Number(p.lat_parada) : NaN;
      const lng = p ? Number(p.lng_parada) : NaN;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const mapRef = this.$refs.guiaMap;
      const map = mapRef && mapRef.mapObject ? mapRef.mapObject : null;
      if (!map) return;
      map.invalidateSize();
      const currentZoom = typeof map.getZoom === 'function' ? map.getZoom() : 14;
      const targetZoom = currentZoom < 16 ? 16 : currentZoom;
      map.setView([lat, lng], targetZoom, { animate: true });
    },
    focusStopFromDrawer(p) {
      this.focusStop(p);
      this.stopsDrawerOpen = false;
    },
    async openGuiaDetalhe(g) {
      if (!this.token) return;
      this.clearCheckWaitTicker();
      this.dialogDetalhe = true;
      this.loadingDetalhe = true;
      this.guiaDetalhe = null;
      this.rotasDetalhe = [];
      this.paradasDetalhe = [];

      try {
        const res = await apiController.request(apiController.endpoints.getGuiamotoristarota, {
          params: { id_guia: g.id_guia },
          silent: true,
        });

        const payload = res && res.data ? res.data : {};
        this.guiaDetalhe = {
          ...(g || {}),
          ...(payload.guia || {}),
          veiculo: payload.veiculo || (payload.guia && payload.guia.veiculo) || null,
        };
        this.syncGuiaVeiculoPosicaoFromDetalhe();

        const rotas = Array.isArray(payload.rotas) ? payload.rotas : [];
        this.rotasDetalhe = rotas
          .map((r) => {
            const geo = r && r.geojson ? r.geojson : null;
            if (!geo) return null;
            if (typeof geo === 'string') {
              try {
                r.geojson = JSON.parse(geo);
              } catch (e) {
                r.geojson = null;
              }
            }
            return r && r.geojson ? r : null;
          })
          .filter(Boolean);

        const paradas = Array.isArray(payload.paradas) ? payload.paradas : [];
        this.paradasDetalhe = paradas
          .map((p) => {
            const lat = Number(p.lat_parada);
            const lng = Number(p.lng_parada);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            return p;
          })
          .filter(Boolean);
        this.paradasDetalhe.sort((a, b) => {
          const oaRaw = a && (a.ordem_parada != null ? a.ordem_parada : a.cod_parada);
          const obRaw = b && (b.ordem_parada != null ? b.ordem_parada : b.cod_parada);
          const oa = Number(oaRaw);
          const ob = Number(obRaw);
          const va = Number.isFinite(oa) ? oa : Number.POSITIVE_INFINITY;
          const vb = Number.isFinite(ob) ? ob : Number.POSITIVE_INFINITY;
          if (va !== vb) return va - vb;
          return String(a.id_parada || '').localeCompare(String(b.id_parada || ''));
        });

        // Default direction based on guia.id_rota when possible
        const idRotaGuia = this.guiaDetalhe && this.guiaDetalhe.id_rota != null ? this.guiaDetalhe.id_rota : null;
        if (idRotaGuia != null && this.rotasDetalhe.length) {
          const r0 = this.rotasDetalhe.find((r) => r && r.id_rota == idRotaGuia);
          if (r0 && r0.id_sentido != null && r0.id_sentido !== '') {
            this.sentidoSelecionado = Number(r0.id_sentido);
          }
        }

        this.$nextTick(() => this.fitGuiaMap());
      } catch (e) {
        const msg =
          e.response && e.response.data && e.response.data.mensagem
            ? e.response.data.mensagem
            : this.$t('guiasMotorista.loadTripDetailsError');
        EventBus.$emit('show-alert', { mensagem: msg, tipo: 'error' });
        this.closeGuiaDetalhe();
      } finally {
        this.loadingDetalhe = false;
        this.$nextTick(() => this.maybeStartCheckWaitTicker());
      }
    },
    onGuiaMapReady() {
      this.$nextTick(() => this.fitGuiaMap());
    },
    firstLatLngFromGeojson(geo) {
      try {
        if (!geo) return null;
        // Feature wrapper
        if (geo.type === 'Feature' && geo.geometry) return this.firstLatLngFromGeojson(geo.geometry);
        // Geometry wrappers / common variants
        if (geo.type === 'LineString' && Array.isArray(geo.coordinates) && geo.coordinates.length) {
          const c = geo.coordinates[0];
          if (Array.isArray(c) && c.length >= 2) return [Number(c[1]), Number(c[0])];
        }
        if (geo.type === 'MultiLineString' && Array.isArray(geo.coordinates) && geo.coordinates.length) {
          const firstLine = geo.coordinates[0];
          const c = Array.isArray(firstLine) && firstLine.length ? firstLine[0] : null;
          if (Array.isArray(c) && c.length >= 2) return [Number(c[1]), Number(c[0])];
        }
        // FeatureCollection
        if (geo.type === 'FeatureCollection' && Array.isArray(geo.features)) {
          for (let i = 0; i < geo.features.length; i += 1) {
            const ll = this.firstLatLngFromGeojson(geo.features[i]);
            if (ll && Number.isFinite(ll[0]) && Number.isFinite(ll[1])) return ll;
          }
        }
      } catch (e) {
        /* ignore */
      }
      return null;
    },
    fitGuiaMap() {
      const mapRef = this.$refs.guiaMap;
      const map = mapRef && mapRef.mapObject ? mapRef.mapObject : null;
      if (!map) return;
      map.invalidateSize();

      // Focus on the first position of the route (start point)
      const rota0 = this.rotasFiltradas && this.rotasFiltradas.length ? this.rotasFiltradas[0] : null;
      const ll0 = rota0 && rota0.geojson ? this.firstLatLngFromGeojson(rota0.geojson) : null;
      if (ll0 && Number.isFinite(ll0[0]) && Number.isFinite(ll0[1])) {
        map.setView(ll0, 15);
        return;
      }

      let bounds = null;
      this.rotasFiltradas.forEach((r) => {
        if (!r || !r.geojson) return;
        try {
          const b = L.geoJSON(r.geojson).getBounds();
          if (!b || !b.isValid || (typeof b.isValid === 'function' && !b.isValid())) return;
          bounds = bounds ? bounds.extend(b) : b;
        } catch (e) {
          /* ignore */
        }
      });

      const pts = this.paradasFiltradas
        .map((p) => [Number(p.lat_parada), Number(p.lng_parada)])
        .filter((ll) => ll && ll.length === 2 && Number.isFinite(ll[0]) && Number.isFinite(ll[1]));
      if (pts.length) {
        const b2 = L.latLngBounds(pts);
        bounds = bounds ? bounds.extend(b2) : b2;
      }

      if (!bounds) {
        const c = this.centerDefault;
        if (c) map.setView(c, 15);
        return;
      }
      if (bounds.isValid && typeof bounds.isValid === 'function' && !bounds.isValid()) {
        const c = this.centerDefault;
        if (c) map.setView(c, 15);
        return;
      }
      map.fitBounds(bounds, { padding: [18, 18], maxZoom: 16 });
    },
    async logout() {
      const raw = sessionStorage.getItem('user');
      let registro = { id_usuario: -1, logado: 0, token: null };
      if (raw) {
        try {
          const u = JSON.parse(raw);
          if (u && u.id_usuario != null) {
            registro = { id_usuario: u.id_usuario, logado: 0, token: null };
          }
        } catch (e) {
          /* ignore */
        }
      }
      try {
        await apiController.request(apiController.endpoints.atualizastatus, { data: registro, silent: true });
      } catch (e) {
        /* ignore */
      }
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('api');
      sessionStorage.removeItem('permissoes');
      this.$router.push({ path: '/' });
    },
  },
};
</script>

<style scoped>
.guia-detalhe-card {
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.guia-detalhe-card :deep(.v-toolbar) {
  flex: 0 0 auto;
}

.guia-detalhe-toolbar-title {
  flex: 0 1 auto;
  max-width: min(100%, 56vw);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
}

.guia-detalhe-toolbar-nav-placeholder {
  width: 48px;
  min-width: 48px;
  flex-shrink: 0;
}

.guia-detalhe-card :deep(.v-progress-linear) {
  flex: 0 0 auto;
}

.guia-detalhe-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: #fff;
}

.guia-detalhe-content {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 0;
}

.guia-detalhe-content--wide {
  flex-direction: row;
  gap: 12px;
  align-items: stretch;
}

.guia-detalhe-map-container {
  flex: 1 1 auto;
  min-height: 0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  z-index: 0;
}

.guia-detalhe-map-container :deep(.leaflet-container) {
  z-index: 0;
}

.guia-detalhe-map {
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.guia-detalhe-stops-panel {
  flex: 0 0 min(380px, 38vw);
  min-width: 280px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-left: 4px;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
}

.guia-stops-fab {
  position: absolute !important;
  right: 12px;
  bottom: 12px;
  z-index: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
}

.guia-stops-drawer {
  z-index: 5000 !important;
  top: 0 !important;
  height: 100% !important;
  max-height: 100% !important;
}

.guia-stops-drawer :deep(.v-navigation-drawer__content) {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.guia-stops-drawer-body {
  flex: 1 1 auto;
  min-height: 0;
}

.stops-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.mapa-navegacao-card {
  height: 100vh;
  min-height: 0;
}

.mapa-navegacao-body {
  min-height: 0;
  overflow: hidden;
}

.nova-guia-card {
  height: 100vh;
  min-height: 0;
  overflow: hidden;
}

.nova-guia-toolbar {
  flex: 0 0 auto !important;
}

.nova-guia-card :deep(.v-progress-linear) {
  flex: 0 0 auto;
}

.nova-guia-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.nova-guia-actions {
  flex: 0 0 auto;
  padding: 8px 16px 16px;
}

.nova-guia-submit-btn {
  flex: 0 0 auto !important;
  height: 48px !important;
  min-height: 48px !important;
  max-height: 48px !important;
}

.nova-guia-submit-btn :deep(.v-btn__content) {
  height: 48px;
}

.guia-card--checkin-ativo {
  border-color: #4caf50 !important;
  border-width: 2px !important;
}

.historico-toolbar {
  flex: 0 0 auto !important;
}

.historico-card {
  height: 100vh;
  min-height: 0;
  overflow: hidden;
}

.historico-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

</style>
